import { styled } from '@zoopla-eng/zds-components/themes';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { Box } from '@zoopla-eng/zds-components/box';
import { Link } from '@zoopla-eng/zds-components/link';
import { Divider } from '@zoopla-eng/zds-components/divider';

import {
    TwitterMedium,
    FacebookMedium,
    LinkedinMedium,
    InstagramMedium,
    YoutubeMedium,
    PinterestMedium
} from '@zoopla-eng/zds-components/icons';
import triggerTracking from '@blueprint/tracking/dist/Tracking';
import { FC } from 'react';
import { Props } from './Footer.types';

const List = styled('ul', {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '$S400',
    '@M100': {
        rowGap: '$S500'
    }
});

const ListItem = styled('li', {
    listStyle: 'none'
});

const LegalText = styled('p', {
    color: '$textBody',
    fontGroup: 'knife'
});

const MobileOnlyDivider = () => (
    <Box pt="$S500" bpM100={{ display: 'none' }}>
        <Divider direction="horizontal" />
    </Box>
);

const GATracking = (eventLabel: string, clickthrough = true) => {
    triggerTracking({
        event: 'GAEvent',
        eventAction: clickthrough ? 'clickthrough' : 'clickout',
        eventLabel: eventLabel,
        eventCategory: 'footer'
    });
};

type LinkType = {
    linkUrl: string;
    linkCopy: string;
    clickThrough?: boolean;
    external?: boolean;
};
const forSaleLinks: LinkType[] = [
    { linkUrl: '/for-sale/', linkCopy: 'For sale' },
    { linkUrl: '/new-homes/', linkCopy: 'New homes' },
    {
        linkUrl: '/for-sale/commercial/',
        linkCopy: 'Commercial properties for sale'
    },
    { linkUrl: '/overseas/', linkCopy: 'Overseas' },
    { linkUrl: '/find-agents/estate-agents/', linkCopy: 'Find estate agents' },
    { linkUrl: '/travel-time/', linkCopy: 'Travel time search' }
];

const toRentLinks: LinkType[] = [
    { linkUrl: '/to-rent/', linkCopy: 'To rent' },
    {
        linkUrl: '/to-rent/commercial/',
        linkCopy: 'Commercial properties to rent'
    },
    { linkUrl: '/find-agents/letting-agents/', linkCopy: 'Find letting agents' }
];

const newsAndGuidesLinks: LinkType[] = [
    { linkUrl: '/discover/', linkCopy: 'News and guides' },
    { linkUrl: '/schemes/help-to-buy/', linkCopy: 'Help to Buy' },
    { linkUrl: '/schemes/shared-ownership/', linkCopy: 'Shared Ownership' },
    { linkUrl: '/buying-a-home/', linkCopy: 'Buying guides' },
    { linkUrl: '/selling-a-home/', linkCopy: 'Selling guides' },
    { linkUrl: '/discover/renting/', linkCopy: 'Renting guides' }
];

const zooplaForBusiness: LinkType[] = [
    { linkUrl: '/for-agents-and-developers/', linkCopy: 'List with Zoopla' },
    {
        linkUrl: 'https://pro.zoopla.co.uk/',
        linkCopy: 'ZooplaPro',
        clickThrough: false
    },
    {
        linkUrl: 'https://www.altosoftware.co.uk/',
        linkCopy: 'Alto Software',
        clickThrough: false,
        external: true
    }
];

const corporateLinks: LinkType[] = [
    { linkUrl: '/privacy/', linkCopy: 'Privacy' },
    { linkUrl: '/privacy/cookie-policy/', linkCopy: 'Cookies' },
    {
        linkUrl: 'https://help.zoopla.co.uk/hc/en-gb',
        linkCopy: 'Contact us',
        clickThrough: false
    },
    { linkUrl: '/about/', linkCopy: 'About Zoopla' },
    {
        linkUrl: 'https://careers.zoopla.co.uk/',
        linkCopy: 'Careers',
        clickThrough: false
    },
    {
        linkUrl: '/display/',
        linkCopy: 'Display advertising',
        clickThrough: false
    },
    { linkUrl: '/sitemap/', linkCopy: 'Sitemap' }
];

const corporateLinks2: LinkType[] = [
    { linkUrl: '/terms/', linkCopy: 'Terms' },
    { linkUrl: '/members/terms/', linkCopy: 'Member terms' },
    { linkUrl: '/slavery-statement/', linkCopy: 'Slavery statement' },
    {
        linkUrl: 'https://careers.zoopla.co.uk/tax-strategy/',
        linkCopy: 'Tax strategy',
        clickThrough: false
    }
];

const socialLinks = [
    {
        linkUrl: 'https://www.facebook.com/ZooplaUK',
        icon: <FacebookMedium />,
        label: 'Visit the Zoopla Facebook Page'
    },
    {
        linkUrl: 'https://twitter.com/zoopla',
        icon: <TwitterMedium />,
        label: 'Visit the Zoopla Twitter Page'
    },
    {
        linkUrl: 'https://www.linkedin.com/company/zoopla/about',
        icon: <LinkedinMedium />,
        label: 'Visit the Zoopla LinkedIn Page'
    },
    {
        linkUrl: 'https://www.instagram.com/zoopla',
        icon: <InstagramMedium />,
        label: 'Visit the Zoopla Instagram Page'
    },
    {
        linkUrl: 'https://www.youtube.com/user/ZooplaUK',
        icon: <YoutubeMedium />,
        label: 'Visit the Zoopla Facebook Page'
    },
    {
        linkUrl: 'https://uk.pinterest.com/zoopla',
        icon: <PinterestMedium />,
        label: 'Visit the Zoopla Pinterest Page'
    }
];

const IconWrapper = styled('a', {
    '&:focus': {
        borderRadius: '$200',
        focusRing: '$functionalFocus'
    }
});

const SocialLinkIcon = ({
    linkUrl,
    icon,
    label
}: {
    linkUrl: string;
    icon: JSX.Element;
    label: string;
}) => (
    <IconWrapper
        target="_blank"
        rel="nofollow"
        href={linkUrl}
        aria-label={label}
    >
        {icon}
    </IconWrapper>
);

const LinkHeader = styled('span', {
    fontWeight: '$400',
    py: '$sizes$XS200',
    color: '$linkTextDefault'
});

const ListItemLink = ({
    linkUrl,
    linkCopy,
    clickThrough = true,
    unweighted,
    external = false
}: LinkType & { unweighted?: boolean }) => (
    <ListItem>
        <Link
            href={linkUrl}
            rel={external ? 'nofollow' : undefined}
            unweighted={unweighted}
            muted
            onClick={() => GATracking(linkCopy.toLowerCase(), clickThrough)}
            disableVisited
        >
            {linkCopy}
        </Link>
    </ListItem>
);

export const Footer: FC<React.PropsWithChildren<Props>> = () => (
    <Box
        display="flex"
        bg="$graphicBackgroundAccentLight"
        justifyContent="center"
        px="$space$viewportS100PageMargin"
        bpM100={{
            px: '$space$viewportM100PageMargin'
        }}
        bpL100={{
            px: '$space$viewportL100PageMargin'
        }}
        py="$M100"
    >
        <Box maxWidth={'$viewportMaxWidth'}>
            <Grid
                gridRowGap="$S500"
                gridTemplateAreas={`
                            "forSale"
                            "toRent"
                            "newsAndGuides"
                            "zooplaForBusiness"
                            "divider"
                            "corporateLinks1"
                            "corporateLinks2"
                            "social"
                            "copyright"
                            "legal"
                        `}
                bpM100={{
                    gridTemplateColumns: 'repeat(12, 1fr)',
                    gridTemplateAreas: `
                        "forSale forSale forSale toRent toRent toRent newsAndGuides newsAndGuides newsAndGuides zooplaForBusiness zooplaForBusiness zooplaForBusiness"
                        "divider divider divider divider divider divider divider divider divider divider divider divider"
                        "corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1"
                        "corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2"
                        "social social social social social social social social social social social social"
                        "copyright copyright copyright copyright copyright copyright copyright copyright copyright copyright copyright copyright"
                        "legal legal legal legal legal legal legal legal legal legal legal legal"
                        `,
                    gridRowGap: '$M300',
                    gridColumnGap: '$S200'
                }}
                bpL100={{
                    gridTemplateAreas: `
                        "forSale forSale forSale toRent toRent toRent newsAndGuides newsAndGuides newsAndGuides zooplaForBusiness zooplaForBusiness zooplaForBusiness"
                        "divider divider divider divider divider divider divider divider divider divider divider divider"
                        "corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 corporateLinks1 social social social social"
                        "corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 corporateLinks2 copyright copyright copyright copyright"
                        "legal legal legal legal legal legal legal legal legal legal legal legal"                        `
                }}
            >
                <Grid.Item area="forSale">
                    <List>
                        {forSaleLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </List>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="toRent">
                    <List>
                        {toRentLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </List>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="newsAndGuides">
                    <List>
                        {newsAndGuidesLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </List>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="zooplaForBusiness">
                    <List>
                        <ListItem>
                            <LinkHeader>Zoopla for business</LinkHeader>
                        </ListItem>
                        {zooplaForBusiness.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={true}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </List>
                </Grid.Item>
                <Grid.Item area="divider">
                    <Divider direction="horizontal" />
                </Grid.Item>
                <Grid.Item area="corporateLinks1">
                    <Box
                        as="ul"
                        display="flex"
                        flexDirection="column"
                        columnGap="$S400"
                        rowGap="$S400"
                        bpM100={{ flexDirection: 'row' }}
                    >
                        {corporateLinks.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                            />
                        ))}
                    </Box>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="corporateLinks2">
                    <Box
                        as="ul"
                        display="flex"
                        flexDirection="column"
                        columnGap="$S400"
                        rowGap="$S400"
                        pb="$S300"
                        bpM100={{ flexDirection: 'row', pb: 0 }}
                    >
                        {corporateLinks2.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                            />
                        ))}
                    </Box>
                </Grid.Item>
                <Grid.Item area="social">
                    <Box
                        display="flex"
                        fw
                        columnGap="$S500"
                        pb="$S300"
                        bpM100={{ pb: 0 }}
                        bpL100={{ justifyContent: 'right' }}
                    >
                        {socialLinks.map(({ icon, linkUrl, label }) => (
                            <SocialLinkIcon
                                key={linkUrl}
                                linkUrl={linkUrl}
                                icon={icon}
                                label={label}
                            />
                        ))}
                    </Box>
                </Grid.Item>
                <Grid.Item area="copyright">
                    <Box
                        fw
                        pb="$S200"
                        bpM100={{ pb: 0 }}
                        bpL100={{
                            display: 'flex',
                            justifyContent: 'right'
                        }}
                    >
                        <LegalText>
                            © 2022 Zoopla Limited. All rights reserved.
                        </LegalText>
                    </Box>
                </Grid.Item>
                <Grid.Item area="legal">
                    <LegalText>
                        Sold house prices provided by Land Registry/Registers of
                        Scotland. © Crown copyright 2022. Our website is
                        completely free for you to use but we may receive a
                        commission from some of the companies we link to on the
                        site. *Zoopla Limited is an appointed representative of
                        Uswitch Limited which is authorised and regulated by the
                        Financial Conduct Authority (FRN 312850) to provide this
                        mortgage comparison service and incorporated with
                        company registration number 06074771 and registered
                        office at The Cooperage, 5 Copper Row, London SE1 2LH.
                        **Uswitch Limited is authorised and regulated by the
                        Financial Conduct Authority (FCA) under firm reference
                        number 312850
                    </LegalText>
                </Grid.Item>
            </Grid>
        </Box>
    </Box>
);
